import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { PageContentWrapper } from '@unwrittenmedia/simple-components'
import SimplePageHeader2 from '@/components/SimplePageHeader2'
import ScreensGrid from '@/components/ScreensGrid'
import type { PlaceCardData } from '@/components/PlaceCard'
import { useGQL_teams } from '@/contexts/graphql-and-swr-apis'
import { MessagesArea } from '@/components/cards/MessagesArea'
import type { TAPITeam } from '@/contexts/graphql-and-swr-apis'
import { useMemo } from 'react'
import { SimpleButton, FunDropdownMenu } from '@unwrittenmedia/simple-components'
import type { FunMenuItem } from '@unwrittenmedia/simple-components'
import { faGlobe, faUsers, faCreditCard } from '@fortawesome/pro-regular-svg-icons'
import { createAuthGuard } from '@/lib/authGuard'

export const Route = createFileRoute('/today')({
  beforeLoad: createAuthGuard(),
  component: TodayPage,
})

const teamMenuItems: FunMenuItem[] = [
  { id: 'public', label: 'View public page', icon: faGlobe },
  { id: 'edit', label: 'Manage', icon: faUsers },
]

const TODAY_MESSAGE_KEYS = ['today']

function TodayPage() {
  const navigate = useNavigate();
  const teamInfos = useGQL_teams({withPlaceStatus: true});

  const sortedTeams = useMemo(() => {
    const teams = teamInfos.data?.teams || []
    return [...teams].filter(t => !t.deleted).sort((a, b) => a.name.localeCompare(b.name))
  }, [teamInfos.data])

  const getTeamPlaces = (team: TAPITeam): PlaceCardData[] =>
    (team.places || []).map(place => ({
      ...place,
      teamId: team.id,
      encoderStatus: place?.encoderOnline ? 'active' : 'inactive',
      isAutoSlides: true,
      thumbnailUrl: undefined,
    }))

  const handlePlaceStartStop = (placeId: string) => {
    console.log(`Start/Stop action for place: ${placeId}`)
    // TODO: Implement actual start/stop logic
  }

  const handlePlaceToggleAutoSlides = (placeId: string) => {
    console.log(`Toggle auto/manual slides for place: ${placeId}`)
    // TODO: Implement actual toggle logic
  }

  const handlePlaceMenuAction = (action: string, placeId: string) => {
    console.log(`Menu action: ${action}, Place ID: ${placeId}`)
    // TODO: Implement actual menu actions
  }

  const handleTeamMenuAction = (action: string, team: TAPITeam) => {
    if (action === 'edit') {
      navigate({ to: '/team/$teamId/manage', params: { teamId: team.id } })
    } else if (action === 'public') {
      console.log('View public page for team:', team.id)
      window.open(`/t/${team.id}`, '_blank')
      // TODO: implement view public page logic
    }
  }

  return (
    <PageContentWrapper>
      <SimplePageHeader2
        title="Today"
        description="View and manage all screens."
        actionButtonText="Add Team"
        actionButtonOnClick={() => navigate({ to: '/team/add-team' })}
      />

      <MessagesArea whereKeys={TODAY_MESSAGE_KEYS} />

      <div className="mt-8 space-y-10">
        {sortedTeams.map(team => (
          <div key={team.id}>
            <div className="flex items-center justify-between mb-4">
              <Link to={`/team/${team.id}/manage`}>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{team.name}</h2>
              </Link>
              <div className="flex items-center gap-3">
                <SimpleButton
                  onClick={() => navigate({ to: '/places/new', search: { team: team.id } })}
                  color="secondary"
                  size="sm"
                >
                  Add Screen
                </SimpleButton>
                <FunDropdownMenu
                  items={teamMenuItems}
                  onAction={handleTeamMenuAction}
                  user={team}
                />
              </div>
            </div>
            <ScreensGrid
              places={getTeamPlaces(team)}
              onStartStop={handlePlaceStartStop}
              onToggleAutoSlides={handlePlaceToggleAutoSlides}
              onMenuAction={handlePlaceMenuAction}
              emptyCreationLink={`/places/new?team=${team.id}`}
            />
          </div>
        ))}
      </div>
    </PageContentWrapper>
  )
}
